// Link preview action for Svelte — supports stacked/chained previews
// Fetches preview data on demand from /previews/{path}.json
import type { PreviewContent, PreviewStackItem } from '$stores/preview.js';
import { previewStack as previewStackStore } from '$stores/preview.js';
import { getCachedArticle } from '$stores/content.js';

export type { PreviewStackItem };

let hoverTimeout: ReturnType<typeof setTimeout> | null = null;
let hideTimeout: ReturnType<typeof setTimeout> | null = null;

// Track which preview levels have the mouse inside
let mouseInsideLevels: Set<number> = new Set();
// Current stack state (module-level, mirrored to the store)
let stack: PreviewStackItem[] = [];
// The level from which a pending hide is scheduled (-1 = none)
let pendingHideFromLevel: number = -1;

// In-memory cache for fetched previews
const previewCache = new Map<string, PreviewContent | null>();

export function extractSectionHtml(fullHtml: string, sectionId: string): string | null {
  const temp = document.createElement('div');
  temp.innerHTML = fullHtml;
  const target = temp.querySelector(`#${CSS.escape(sectionId)}`);
  if (!target || !/^H[2-6]$/.test(target.tagName)) return null;

  const targetLevel = parseInt(target.tagName[1]);
  const elements: string[] = [];
  let sibling = target.nextElementSibling;
  let count = 0;

  while (sibling && count < 5) {
    // Stop at same or higher level heading
    if (/^H[2-6]$/.test(sibling.tagName) && parseInt(sibling.tagName[1]) <= targetLevel) break;
    // Skip note-preview-source divs
    if (sibling.classList.contains('note-preview-source')) {
      sibling = sibling.nextElementSibling;
      continue;
    }
    elements.push(sibling.outerHTML);
    count++;
    sibling = sibling.nextElementSibling;
  }

  return elements.length > 0 ? elements.join('') : null;
}

export async function fetchPreview(articlePath: string): Promise<PreviewContent | null> {
  if (previewCache.has(articlePath)) {
    return previewCache.get(articlePath)!;
  }

  // For anchor paths (e.g. "wiki/glossary#action"), try the per-section file first
  // (used for glossary term previews). For regular section anchors in articles the
  // per-section file won't exist; tryFetch handles that gracefully.
  const fetchPath = articlePath.includes('#')
    ? (() => { const [b, a] = articlePath.split('#'); return `${b}/${a}`; })()
    : articlePath;

  // Fetch a preview JSON file, returning null on any failure (network error,
  // non-200 status, or non-JSON response such as an SPA-fallback HTML page).
  async function tryFetch(path: string): Promise<PreviewContent | null> {
    try {
      const response = await fetch(`/previews/${path}.json`);
      if (!response.ok) return null;
      const data = await response.json();
      return {
        title: data.title,
        excerptHtml: data.excerptHtml || '',
        infoboxHtml: data.infoboxHtml || undefined,
        previewImage: data.previewImage || undefined,
        path: articlePath,
      };
    } catch {
      return null;
    }
  }

  // Try per-section path first; fall back to base article for anchor paths.
  let preview = await tryFetch(fetchPath);
  if (!preview && articlePath.includes('#')) {
    preview = await tryFetch(articlePath.split('#')[0]);
  }

  // For anchor paths, try to extract section-specific content (browser only)
  if (preview && articlePath.includes('#') && typeof document !== 'undefined') {
    const [basePath, anchor] = articlePath.split('#');
    // Use cached article if available; otherwise fetch HTML directly
    // (don't use loadArticle — it mutates currentArticle store)
    let html: string | null = getCachedArticle(basePath)?.html ?? null;
    if (!html) {
      try {
        const resp = await fetch(`/articles/${basePath}.json`);
        if (resp.ok) {
          const data = await resp.json();
          html = data.html ?? null;
        }
      } catch { /* ignore */ }
    }
    if (html) {
      const sectionHtml = extractSectionHtml(html, anchor);
      if (sectionHtml) {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        const heading = temp.querySelector(`#${CSS.escape(anchor)}`);
        const sectionTitle = heading?.textContent?.trim() || preview.title;
        preview = {
          ...preview,
          title: sectionTitle,
          excerptHtml: sectionHtml,
          infoboxHtml: undefined,
          previewImage: undefined,
        };
      }
    }
  }

  previewCache.set(articlePath, preview);
  return preview;
}

function extractArticlePath(link: HTMLAnchorElement): string | null {
  const href = link.getAttribute('href');
  if (!href) return null;
  const path = href.replace(/^#\//, '');
  if (path.startsWith('volumes/') || path.startsWith('wiki/')) {
    return path;
  }
  return null;
}

function cancelHide() {
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }
  pendingHideFromLevel = -1;
}

function emitStack() {
  previewStackStore.set([...stack]);
}

function clearAll() {
  stack = [];
  mouseInsideLevels.clear();
  emitStack();
}

function scheduleHideFrom(level: number) {
  cancelHide();
  pendingHideFromLevel = level;
  hideTimeout = setTimeout(() => {
    pendingHideFromLevel = -1;
    // Check if mouse is in any level at or above the one being hidden
    let mouseInAny = false;
    for (let i = level; i < stack.length; i++) {
      if (mouseInsideLevels.has(i)) {
        mouseInAny = true;
        break;
      }
    }
    if (!mouseInAny) {
      stack = stack.slice(0, level);
      // If we trimmed to some levels, check if mouse is still in any remaining level
      if (stack.length > 0) {
        let mouseInRemaining = false;
        for (let i = 0; i < stack.length; i++) {
          if (mouseInsideLevels.has(i)) {
            mouseInRemaining = true;
            break;
          }
        }
        if (!mouseInRemaining) {
          stack = [];
        }
      }
      emitStack();
    }
  }, 150);
}

// ── Module-level control functions (used by Layout.svelte) ──────────────────

export function dismissAll() {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout);
    hoverTimeout = null;
  }
  cancelHide();
  clearAll();
}

export function onPreviewMouseEnter(level: number) {
  mouseInsideLevels.add(level);
  if (pendingHideFromLevel >= 0 && level >= pendingHideFromLevel) {
    cancelHide();
  }
}

export function onPreviewMouseLeave(level: number) {
  mouseInsideLevels.delete(level);
  scheduleHideFrom(level);
}

export async function onPreviewLinkHover(
  level: number,
  link: HTMLAnchorElement,
  parentEl: HTMLElement,
) {
  cancelHide();
  const articlePath = extractArticlePath(link);
  const preview = articlePath ? await fetchPreview(articlePath) : null;
  const linkRect = link.getBoundingClientRect();
  const parentRect = parentEl.getBoundingClientRect();
  stack = stack.slice(0, level + 1);
  stack.push({ preview, linkRect, parentRect });
  for (const l of mouseInsideLevels) {
    if (l > level) mouseInsideLevels.delete(l);
  }
  emitStack();
}

// ── Setup / teardown ────────────────────────────────────────────────────────

export function setupLinkPreviews(container: HTMLElement): () => void {
  stack = [];
  mouseInsideLevels.clear();
  emitStack();

  function showNoteTriggerPreview(trigger: HTMLElement) {
    const noteId = trigger.getAttribute('data-note-id');
    if (!noteId) return;
    const noteEl = container.querySelector(`#note-preview-${CSS.escape(noteId)}`);
    if (!noteEl) return;
    const excerptHtml = noteEl.querySelector('.note-content')?.innerHTML || '';
    const title = noteEl.getAttribute('data-note-title') || '';
    const preview: PreviewContent = { title, excerptHtml, path: '' };
    const linkRect = trigger.getBoundingClientRect();
    stack = [{ preview, linkRect, parentRect: null }];
    mouseInsideLevels.clear();
    emitStack();
  }

  function handleMouseEnter(event: Event) {
    const target = event.target as HTMLElement;

    // Note preview triggers
    const noteTrigger = target.closest('.note-trigger[data-note-id]') as HTMLElement | null;
    if (noteTrigger) {
      cancelHide();
      if (hoverTimeout) { clearTimeout(hoverTimeout); hoverTimeout = null; }
      hoverTimeout = setTimeout(() => { showNoteTriggerPreview(noteTrigger); }, 300);
      return;
    }

    const link = target.closest('a[href^="#/"]') as HTMLAnchorElement | null;

    if (!link) return;
    if (link.closest('.link-preview')) return;
    if (link.classList.contains('heading-anchor')) return;

    const articlePath = extractArticlePath(link);
    if (!articlePath) return;

    cancelHide();

    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    if (stack.length > 0) {
      const linkRect = link.getBoundingClientRect();
      stack = [{ preview: stack[0].preview, linkRect, parentRect: null }];
      mouseInsideLevels.clear();
      emitStack();
    }

    hoverTimeout = setTimeout(async () => {
      const preview = await fetchPreview(articlePath);
      const linkRect = link.getBoundingClientRect();

      stack = [{ preview, linkRect, parentRect: null }];
      mouseInsideLevels.clear();
      emitStack();
    }, stack.length > 0 ? 100 : 300);
  }

  function handleMouseLeave(event: Event) {
    const target = event.target as HTMLElement;

    // Note trigger mouse leave
    const noteTrigger = target.closest('.note-trigger[data-note-id]') as HTMLElement | null;
    if (noteTrigger) {
      if (hoverTimeout) { clearTimeout(hoverTimeout); hoverTimeout = null; }
      scheduleHideFrom(0);
      return;
    }

    const link = target.closest('a[href^="#/"]');

    if (!link) return;
    if (link.closest('.link-preview')) return;
    if ((link as HTMLElement).classList.contains('heading-anchor')) return;

    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = null;
    }

    scheduleHideFrom(0);
  }

  function handleClick(event: Event) {
    const target = event.target as HTMLElement;

    // Note trigger: tap to show/dismiss popup
    const noteTrigger = target.closest('.note-trigger[data-note-id]') as HTMLElement | null;
    if (noteTrigger) {
      (event as MouseEvent).preventDefault?.();
      if (hoverTimeout) { clearTimeout(hoverTimeout); hoverTimeout = null; }
      cancelHide();
      if (stack.length > 0) {
        clearAll();
      } else {
        showNoteTriggerPreview(noteTrigger);
      }
      return;
    }

    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = null;
    }
    cancelHide();
    clearAll();
  }

  function handleScroll() {
    if (stack.length > 0) {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
      cancelHide();
      clearAll();
    }
  }

  container.addEventListener('mouseenter', handleMouseEnter, true);
  container.addEventListener('mouseleave', handleMouseLeave, true);
  container.addEventListener('click', handleClick, true);
  window.addEventListener('scroll', handleScroll, true);

  // Touch support
  let touchTimeout: ReturnType<typeof setTimeout> | null = null;

  function handleTouchStart(event: TouchEvent) {
    const target = event.target as HTMLElement;
    const link = target.closest('a[href^="#/"]') as HTMLAnchorElement | null;

    if (!link) return;

    const articlePath = extractArticlePath(link);
    if (!articlePath) return;

    touchTimeout = setTimeout(async () => {
      event.preventDefault();

      const preview = await fetchPreview(articlePath);
      const linkRect = link.getBoundingClientRect();

      stack = [{ preview, linkRect, parentRect: null }];
      mouseInsideLevels.clear();
      emitStack();
    }, 500);
  }

  function handleTouchEnd() {
    if (touchTimeout) {
      clearTimeout(touchTimeout);
      touchTimeout = null;
    }
  }

  function handleTouchMove() {
    if (touchTimeout) {
      clearTimeout(touchTimeout);
      touchTimeout = null;
    }
  }

  container.addEventListener('touchstart', handleTouchStart, { passive: false });
  container.addEventListener('touchend', handleTouchEnd);
  container.addEventListener('touchmove', handleTouchMove);

  return () => {
    container.removeEventListener('mouseenter', handleMouseEnter, true);
    container.removeEventListener('mouseleave', handleMouseLeave, true);
    container.removeEventListener('click', handleClick, true);
    window.removeEventListener('scroll', handleScroll, true);
    container.removeEventListener('touchstart', handleTouchStart);
    container.removeEventListener('touchend', handleTouchEnd);
    container.removeEventListener('touchmove', handleTouchMove);

    if (hoverTimeout) clearTimeout(hoverTimeout);
    if (hideTimeout) clearTimeout(hideTimeout);
    if (touchTimeout) clearTimeout(touchTimeout);

    stack = [];
    mouseInsideLevels.clear();
    emitStack();
  };
}
